<template>
  <div class="ai-play-unsolicited-effect">
    <div class="el-input-group el-input-group--prepend">
      <div class="el-input-group__prepend">Make</div>
      <character-select
        :selected="state.sourceCharacterKey"
        :characters="getSourceCharacters(emitter.caseConf)"
        :disabled="readonly"
        :errors="sourceCharacterErrors"
        :warnings="sourceCharacterWarnings"
        placeholder="Select Source Character"
        @input="updateSourceCharacter"
      />
    </div>
    <div class="el-input-grid">
      <div class="el-input-group el-input-group--prepend">
        <div class="el-input-group__prepend">Say</div>
        <el-tooltip
          :disabled="!selectedUnsolicited"
          :visible-arrow="false"
          :content="selectedUnsolicited"
          placement="top"
        >
          <el-select
            id="unsolicited-select"
            v-close-on-zoom
            :value="state.effect.id"
            placeholder="Select Statement"
            :filterable="false"
            :default-first-option="true"
            class="unsolicited-select"
            :disabled="readonly"
            @input="selectUnsolicited"
          >
            <el-option
              v-for="unsolicitedItem in getSelectedCharacterItems()"
              :key="unsolicitedItem.id"
              :label="unsolicitedItem.options.answer.en"
              :value="unsolicitedItem.id"
            />
          </el-select>
        </el-tooltip>
      </div>
      <help-tooltip v-if="!state.effect.id && !missingDialogueItemErrors" type="warning">
        An option must be selected. Data will not be saved.
      </help-tooltip>
      <help-tooltip v-if="missingDialogueItemErrors.length" type="error">
        <div v-for="error in missingDialogueItemErrors" :key="error.message">{{ error.message }}</div>
      </help-tooltip>
    </div>
    <div class="el-input-grid">
      <div class="el-input-group el-input-group--append">
        <div class="el-input-group__prepend">To</div>
        <el-tooltip
          :disabled="!state.effect.targetCharacterKey"
          :visible-arrow="false"
          :content="targetCharacterLabel"
          placement="top"
        >
          <character-select
            :selected="state.effect.targetCharacterKey"
            :characters="getTargetCharacters(emitter.caseConf)"
            :disabled="readonly"
            placeholder="Select Target Character"
            @input="updateTargetCharacter"
          />
        </el-tooltip>
      </div>
      <help-tooltip v-if="missingTargetCharacterErrors.length" type="error">
        <div v-for="error in missingTargetCharacterErrors" :key="error.message">{{ error.message }}</div>
      </help-tooltip>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import nodeMixins from './mixins/NodeMixins';
import CharacterSelect from './CharacterSelect.vue';
import HelpTooltip from '../../common/components/HelpTooltip';
import { getSourceCharacters, getTargetCharacters, getCharacter } from '~lib/common/utils/characters';
import { getDialogueItemsForCharacter } from '~lib/common/utils/dialogue';

export default {
  name: 'ai-play-unsolicited-effect',
  components: {
    CharacterSelect,
    HelpTooltip,
  },
  mixins: [ nodeMixins ],
  props: ['readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    return {
      state: {
        sourceCharacterKey: null,
        targetCharacterKey: null,
        effect: {},
        dialogueItems: [],
        selectedItem: null,
      },
    };
  },
  computed: {
    missingDialogueItemErrors() {
      return this.getErrorsById('missingDialogueItem');
    },
    sourceCharacterErrors() {
      return this.getErrorsById('missingSourceCharacter');
    },
    sourceCharacterWarnings() {
      if (!this.state.sourceCharacterKey) {
        return [{ message: 'An option must be selected. Data will not be saved.'}]
      }
      return [];
    },
    missingTargetCharacterErrors() {
      return this.getErrorsById('missingTargetCharacter');
    },
    selectedUnsolicited() {
      const item = (this.state.dialogueItems || []).find(i => i.id === this.state.effect.id);
      return get(item, 'options.answer.en');
    },
    targetCharacterLabel() {
      const targetCharacter = getCharacter(this.emitter.caseConf, this.state.effect.targetCharacterKey) || {};
      return targetCharacter.label;
    },
    sourceCharacterLabel() {
      const sourceCharacter = getCharacter(this.emitter.caseConf, this.state.sourceCharacterKey) || {};
      return sourceCharacter.label;
    },
  },
  mounted() {
    this.syncState();
    this.emitter.on('process', (nodeOrControl) => {
      // Update internal state when the node data changes
      // outside (e.g. from a CallAction)
      if (nodeOrControl === this.control) {
        this.syncState();
      }
    });
    this.emitter.trigger('process', this.control);
  },
  methods: {
    updateSourceCharacter(characterKey) {
      this.state.sourceCharacterKey = characterKey;
      this.state.effect.id = null;
      this.updateTargetCharacter(null)
      this.update();
    },
    updateTargetCharacter(characterKey) {
      this.state.effect.targetCharacterKey = characterKey;
      this.update();
    },
    getSelectedCharacterItems() {
      if (this.state.sourceCharacterKey) {
        const caseConf = this.emitter.caseConf;
        const characterKey = this.state.sourceCharacterKey;
        const dialogueType = 'aiUnsolicited';
        const selectedCharacterDialogue = getDialogueItemsForCharacter(caseConf, characterKey, dialogueType);
        return selectedCharacterDialogue || [];
      }
    },
    syncState() {
      this.state.effect = this.getData(this.ikey) || {};
      // To make legacy effects work, if we have a saved id, set selectedCharacterKey to 'patient'
      if (this.state.effect.id && !this.state.effect.sourceCharacterKey) {
        this.state.sourceCharacterKey = 'patient';
        this.getSelectedCharacterItems();
        this.update();
      }
      else if (this.state.effect.sourceCharacterKey) {
        this.state.sourceCharacterKey = this.state.effect.sourceCharacterKey
      }
      this.getSelectedCharacterItems();
    },
    selectUnsolicited(unsolicitedId) {
      this.state.effect.id = unsolicitedId;
      this.update();
    },
    update() {
      this.state.effect.sourceCharacterKey = this.state.sourceCharacterKey;
      this.putData(this.ikey, { type: 'AI_UNSOLICITED_DIALOGUE', ...this.state.effect });
      this.emitter.trigger('process', this.control);
      this.$forceUpdate();
    },
    getSourceCharacters,
    getTargetCharacters,
    getCharacter,
    getDialogueItemsForCharacter
  },
}
</script>

<style scoped>
.ai-play-unsolicited-effect,
.character-select,
.unsolicited-select {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.el-input-grid {
  display: grid;
  grid-template-columns: 1fr auto;
}
.help-tooltip {
  margin-left: 7px;
  margin-top: 7px;
}
</style>
